import React, { useState, useEffect, useRef } from 'react';
import { useModelStore } from '../../store/useModelStore';

interface BeamPropertiesDialogProps {
  beamId: string;
  visible: boolean;
  position: { x: number; y: number };
  onClose: () => void;
}

/**
 * Enhanced Beam Properties Dialog
 * Allows editing of beam material and display properties
 * Features: Draggable across entire UI
 */
export const BeamPropertiesDialog: React.FC<BeamPropertiesDialogProps> = ({
  beamId,
  visible,
  position,
  onClose
}) => {
  const { beams, updateBeamProperties } = useModelStore();
  
  // Find the beam to edit
  const beam = beams.find(b => b.id === beamId);
  
  // Form state
  const [material, setMaterial] = useState<string>('steel');
  const [color, setColor] = useState<string>('#4dabf7');
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Drag state
  const [dialogPosition, setDialogPosition] = useState(position);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const dialogRef = useRef<HTMLDivElement>(null);

  // Initialize form with current beam properties
  useEffect(() => {
    if (beam) {
      setMaterial(beam.material || 'steel');
      setColor(beam.color || '#4dabf7');
      setError('');
    }
  }, [beam, visible]);

  // Update dialog position when prop changes
  useEffect(() => {
    setDialogPosition(position);
  }, [position]);

  // Handle drag start
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dialogRef.current) {
      const rect = dialogRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  // Handle drag move
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && dialogRef.current) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        
        // Constrain to viewport bounds
        const maxX = window.innerWidth - dialogRef.current.offsetWidth;
        const maxY = window.innerHeight - dialogRef.current.offsetHeight;
        
        setDialogPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleSave = () => {
    if (!beam) {
      setError('Beam not found');
      return;
    }

    try {
      // Update beam properties
      if (updateBeamProperties) {
        updateBeamProperties(beamId, {
          material,
          color
        });
        console.log('✅ Beam properties updated:', { beamId, material, color });
        
        // Show success feedback but don't close - let user close manually
        setError(''); // Clear any existing errors
        setSuccessMessage('✅ Beam properties updated successfully!');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Unable to update beam properties');
      }
    } catch (err) {
      console.error('❌ Error updating beam properties:', err);
      setError('Failed to update beam properties');
    }
  };

  const handleCancel = () => {
    setError('');
    onClose();
  };

  if (!visible || !beam) return null;

  return (
    <div
      ref={dialogRef}
      className={`fixed bg-white dark:bg-gray-800 rounded-lg shadow-xl w-80 z-50 border-2 border-gray-200 dark:border-gray-600 ${
        isDragging ? 'cursor-grabbing shadow-2xl' : 'cursor-auto'
      }`}
      style={{
        left: dialogPosition.x + 'px',
        top: dialogPosition.y + 'px',
      }}
    >
      {/* Draggable Header */}
      <div 
        className={`flex justify-between items-center p-4 pb-2 border-b border-gray-200 dark:border-gray-600 ${
          isDragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        onMouseDown={handleMouseDown}
      >
        <h3 className="text-lg font-semibold dark:text-white select-none">Edit Beam Properties</h3>
        <button
          onClick={handleCancel}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 cursor-pointer"
        >
          ✕
        </button>
      </div>

      {/* Content Area */}
      <div className="p-4">
        <div className="space-y-4">
        {/* Beam ID Display */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Beam ID
          </label>
          <input
            type="text"
            value={beamId}
            disabled
            className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white bg-gray-100 cursor-not-allowed"
          />
        </div>

        {/* Material Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Material
          </label>
          <select
            value={material}
            onChange={(e) => setMaterial(e.target.value)}
            className="w-full px-3 py-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="steel">Steel</option>
            <option value="concrete">Concrete</option>
            <option value="wood">Wood</option>
            <option value="aluminum">Aluminum</option>
            <option value="composite">Composite</option>
          </select>
        </div>

        {/* Color Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Color
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-12 h-8 border rounded dark:bg-gray-700 dark:border-gray-600"
            />
            <input
              type="text"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="flex-1 px-3 py-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="#4dabf7"
            />
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="text-green-600 text-sm bg-green-50 dark:bg-green-900/20 p-2 rounded">
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-2 rounded">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
          >
            Save Changes
          </button>
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-2 text-sm bg-gray-500 hover:bg-gray-600 text-white rounded-md transition-colors"
          >
            Cancel
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};
